import React, { useEffect, useState, useRef } from "react";
import { Building2, Check, AlertCircle, Loader2, Edit2, X } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { API_BASE_URL } from '../services/api';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const BANKS_LIST_URL = `${API_BASE_URL}/master/banks`;
const BANK_SAVE_URL = `${API_BASE_URL}/user/store/bank`;

const BankDetails = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [existingBankDetails, setExistingBankDetails] = useState(null);

  const [formData, setFormData] = useState({
    accountName: "",
    accountNumber: "",
    bankName: "",
    ifsc: "",
  });

  // Map refs
  const viewMapRef = useRef(null);
  const viewMarkerRef = useRef(null);

  /* =======================
     LOAD EXISTING BANK DETAILS FROM LOGIN
     ======================= */
  useEffect(() => {
    // Get user details
    let userDetails = null;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.others) {
          userDetails = JSON.parse(user.others);
          
          // Load existing bank details if present
          if (userDetails.account_name || userDetails.acc_holder) {
            const existingDetails = {
              accountName: userDetails.account_name || userDetails.acc_holder || "",
              accountNumber: userDetails.account_number || userDetails.acc_num || "",
              bankName: userDetails.bank_name || userDetails.bank || "",
              ifsc: userDetails.ifsc_code || userDetails.ifsc || "",
            };
            setExistingBankDetails(existingDetails);
            setFormData(existingDetails);
          }
        }
      }
    } catch (err) {
      console.error("Error loading user details:", err);
    }

    // Only initialize map if we have location and we're in view mode (not editing)
    if (!isEditing && userDetails?.location && typeof window !== "undefined" && L) {
      const timer = setTimeout(() => {
        const mapContainer = document.getElementById("store-location-map");
        
        // Clear any existing map first
        if (viewMapRef.current) {
          viewMapRef.current.remove();
          viewMapRef.current = null;
          viewMarkerRef.current = null;
        }
        
        if (mapContainer) {
          const coords = userDetails.location.split(",");
          if (coords.length === 2) {
            const lat = parseFloat(coords[0].trim());
            const lng = parseFloat(coords[1].trim());
            
            if (!isNaN(lat) && !isNaN(lng)) {
              // Initialize map
              const map = L.map("store-location-map").setView([lat, lng], 13);

              L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                attribution: "© OpenStreetMap contributors",
              }).addTo(map);

              // Add marker with popup
              const marker = L.marker([lat, lng]).addTo(map);
              
              // Get store name for popup
              const userName = JSON.parse(localStorage.getItem("user"))?.name || "Store Location";
              marker.bindPopup(`<b>${userName}</b>`).openPopup();

              viewMapRef.current = map;
              viewMarkerRef.current = marker;
            }
          }
        }
      }, 200);

      return () => {
        clearTimeout(timer);
        if (viewMapRef.current) {
          viewMapRef.current.remove();
          viewMapRef.current = null;
          viewMarkerRef.current = null;
        }
      };
    }

    // Cleanup
    return () => {
      if (viewMapRef.current) {
        viewMapRef.current.remove();
        viewMapRef.current = null;
        viewMarkerRef.current = null;
      }
    };
  }, [isEditing]);

  /* =======================
     FETCH BANK LIST (GET)
     ======================= */
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const response = await fetch(BANKS_LIST_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch banks");
        }

        const result = await response.json();
        setBanks(result.data || []);
      } catch (err) {
        console.error(err);
        setError("Unable to load bank list");
      } finally {
        setLoading(false);
      }
    };

    fetchBanks();
  }, []);

  /* =======================
     FORM CHANGE
     ======================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* =======================
     SAVE BANK DETAILS (PUT)
     ======================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");

      const response = await fetch(BANK_SAVE_URL, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          acc_holder: formData.accountName,
          acc_num: formData.accountNumber,
          bank: formData.bankName,
          ifsc: formData.ifsc,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save bank details");
      }

      const result = await response.json();
      alert("Bank details saved successfully!");

      setExistingBankDetails(formData);
      
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          
          let details = {};
          if (user.others) {
            details = JSON.parse(user.others);
          }
          
          details.acc_holder = formData.accountName;
          details.acc_num = formData.accountNumber;
          details.bank = formData.bankName;
          details.ifsc = formData.ifsc;
          
          user.others = JSON.stringify(details);
          localStorage.setItem("user", JSON.stringify(user));
        }
      } catch (err) {
        console.error("Error updating localStorage:", err);
      }

      setIsEditing(false);
      
      const refetchResponse = await fetch(BANKS_LIST_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (refetchResponse.ok) {
        const refetchResult = await refetchResponse.json();
        setBanks(refetchResult.data || []);
      }
    } catch (err) {
      console.error(err);
      setError("Unable to save bank details");
    } finally {
      setSaving(false);
    }
  };

  /* =======================
     CANCEL EDIT
     ======================= */
  const handleCancelEdit = () => {
    if (existingBankDetails) {
      setFormData(existingBankDetails);
    }
    setIsEditing(false);
  };

  /* =======================
     LOADING STATE
     ======================= */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading store details...</p>
        </div>
      </div>
    );
  }

  /* =======================
     ERROR STATE
     ======================= */
  if (error && !existingBankDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  /* =======================
     GET USER DETAILS
     ======================= */
  let userDetails = null;
  let userName = "";
  let userId = "";
  let userRole = "";

  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      
      if (user.others) {
        userDetails = JSON.parse(user.others);
      }
      
      userName = user?.name || "";
      userId = user?.id || "";
      userRole = user?.role || "";
    }
  } catch (err) {
    console.error("Error loading user details:", err);
  }

  /* =======================
     EDIT MODE / FORM
     ======================= */
  if (isEditing) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {existingBankDetails ? "Update Bank Details" : "Add Bank Details"}
                  </h2>
                </div>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mx-6 mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-semibold">Error</p>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                {/* Account Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Holder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleChange}
                    required
                    placeholder="Enter account holder name"
                    className="w-full px-3 py-2 rounded border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all text-gray-900"
                  />
                </div>

                {/* Account Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    required
                    placeholder="Enter account number"
                    className="w-full px-3 py-2 rounded border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all text-gray-900"
                  />
                </div>

                {/* Bank Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 rounded border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all text-gray-900"
                  >
                    <option value="">Select Bank</option>
                    {banks.map((bank, index) => (
                      <option key={index} value={bank}>
                        {bank}
                      </option>
                    ))}
                  </select>
                </div>

                {/* IFSC Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IFSC Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleChange}
                    required
                    placeholder="Enter IFSC code (e.g., SBIN0001234)"
                    className="w-full px-3 py-2 rounded border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all text-gray-900 uppercase"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8 flex gap-3">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 text-white py-2 rounded font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {existingBankDetails ? "Update Details" : "Save Details"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  /* =======================
     DISPLAY MODE (READ-ONLY) - TWO-COLUMN LAYOUT
     ======================= */
  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Store Details Section */}
        {(userName || userDetails) && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <h2 className="text-2xl font-semibold text-gray-900">Store Details</h2>
            </div>

            <div className="px-6 py-5">
              {/* Two-Column Grid Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-5">
                {/* Left Column */}
                <div className="space-y-5">
                  {/* Type */}
                  {userDetails?.type && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Type:</label>
                      <p className="text-base text-gray-900">{userDetails.type}</p>
                    </div>
                  )}

                  {/* Store ID */}
                  {userDetails?.code && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Store Code:</label>
                      <p className="text-base text-gray-900">{userDetails.code}</p>
                    </div>
                  )}

                  {/* Brand */}
                  {userDetails?.brand && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Brand:</label>
                      <p className="text-base text-gray-900">{userDetails.brand}</p>
                    </div>
                  )}

                  {/* Store Official Name */}
                  {userDetails?.manager && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Store Official Name:</label>
                      <p className="text-base text-gray-900">{userDetails.manager}</p>
                    </div>
                  )}

                  {/* Address */}
                  {userDetails?.address && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Address:</label>
                      <p className="text-base text-gray-900">{userDetails.address}</p>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  {/* Store Name */}
                  {userName && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Store Name:</label>
                      <p className="text-base text-gray-900">{userName}</p>
                    </div>
                  )}

                  {/* District */}
                  {userDetails?.district && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">District:</label>
                      <p className="text-base text-gray-900">{userDetails.district}</p>
                    </div>
                  )}

                  {/* Contact */}
                  {userDetails?.contact && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Contact:</label>
                      <p className="text-base text-gray-900">{userDetails.contact}</p>
                    </div>
                  )}

                  {/* Bank - with Edit Button */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-semibold text-gray-700">Bank:</label>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors text-sm font-medium"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                 
                      </button>
                    </div>
                    <p className="text-base text-gray-900">
                      {existingBankDetails?.bankName || "Not added"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Store Location Map - Full Width */}
              {userDetails?.location && (
                <div className="mt-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Store Location</label>
                  <div 
                    id="store-location-map" 
                    className="w-full h-96 rounded border border-gray-300"
                    style={{ zIndex: 0 }}
                  />
                </div>
              )}

              {/* Bank Details Section */}
              {existingBankDetails && (
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Bank Details</h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-5">
                    {/* Account Name */}
                    {existingBankDetails.accountName && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Account Holder Name:</label>
                        <p className="text-base text-gray-900">{existingBankDetails.accountName}</p>
                      </div>
                    )}

                    {/* Account Number */}
                    {existingBankDetails.accountNumber && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Account Number:</label>
                        <p className="text-base text-gray-900 font-mono">{existingBankDetails.accountNumber}</p>
                      </div>
                    )}

                    {/* Bank Name */}
                    {existingBankDetails.bankName && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Bank Name:</label>
                        <p className="text-base text-gray-900">{existingBankDetails.bankName}</p>
                      </div>
                    )}

                    {/* IFSC Code */}
                    {existingBankDetails.ifsc && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">IFSC Code:</label>
                        <p className="text-base text-gray-900 font-mono uppercase">{existingBankDetails.ifsc}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankDetails;